"use client";

import { c as _c } from "react/compiler-runtime";
import type { SdkArtifactPickerItem } from "@magnus/contract/artifacts";
import type { MagnusUIMessage } from "@magnus/contract/messages";
import type { SdkSkillPickerItem } from "@magnus/contract/skills";
import type { AppSurface, ReasoningLevel } from "@magnus/contract/values";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useMagnusClient } from "./provider";
import { AUTO_MODEL_SELECTION, bindHistoryActions, type ChatHistoryStore, createChatHistoryStore, createDictationStore, createFeedbackStore, createListStore, createModelsStore, createUploadsStore, type DictationStore, type FeedbackStore, type FeedbackStoreState, type ListStore, type MagnusApprovalsSource, type MagnusChatHistorySource, type MagnusDictationSource, type MagnusFeedbackSource, type MagnusModelsSource, type MagnusUploadsSource, type ModelsStore } from "./source-stores";

// ---------------------------------------------------------------------------
// Shared plumbing
// ---------------------------------------------------------------------------

const DEFAULT_REASONING: ReasoningLevel = "medium";
const NOOP_SUBSCRIBE = () => () => {
  // No store → nothing to unsubscribe.
};
interface SnapshotStore<State> {
  getState(): State;
  subscribe(listener: () => void): () => void;
}
function useStoreState<State>(store: SnapshotStore<State> | null, emptyState: State): State {
  // Memoized per store: `useSyncExternalStore` re-subscribes whenever the
  // subscribe identity changes, so a fresh closure per render would tear the
  // listener down and rebuild it on every render. The fallback state is read
  // through a ref to keep both callbacks out of the render-identity churn.
  const emptyRef = useRef(emptyState);
  emptyRef.current = emptyState;
  const subscribe = useMemo(() => store ? (listener: () => void) => store.subscribe(listener) : NOOP_SUBSCRIBE, [store]);
  const getSnapshot = useMemo(() => store ? () => store.getState() : () => emptyRef.current, [store]);
  const getServerSnapshot = useCallback(() => emptyRef.current, []);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

export interface UseMagnusModelsResult {
  /** The level a turn runs at when it sends none. */
  defaultReasoningLevel: ReasoningLevel;
  error?: Error;
  /** Prop-ready source for the magnus-chat block's `models` prop. */
  models: MagnusModelsSource;
  /** The model to send on the next turn, or `undefined` for Auto (let the
   *  server choose). Feed it into `useMagnus`'s `getBody`. */
  selectedModelId?: string;
  /** Whether the model the next turn runs on takes a thinking level. */
  supportsReasoningLevel: boolean;
}

/**
 * The composer's model picker over `magnus.models.list()`. Selection
 * persists in localStorage under the same keys the web app uses (only
 * when the host's "remember my model" setting is on), so the web app and
 * SDK embeds on the same origin share the preference.
 */
export function useMagnusModels() {
  const $ = _c(24);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = createModelsStore({
      client
    });
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const store = t0;
  let t1;
  if ($[2] !== store) {
    t1 = store.getState();
    $[2] = store;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const state = useStoreState(store, t1);
  const isAuto = state.selection === AUTO_MODEL_SELECTION;
  const effectiveModelId = isAuto ? state.defaultId : state.selection;
  let t2;
  if ($[4] !== effectiveModelId || $[5] !== isAuto || $[6] !== state.autoRouted || $[7] !== state.items) {
    t2 = !(isAuto && state.autoRouted) && state.items.some(model => model.id === effectiveModelId && model.supportsReasoningLevel);
    $[4] = effectiveModelId;
    $[5] = isAuto;
    $[6] = state.autoRouted;
    $[7] = state.items;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const supportsReasoningLevel = t2;
  let t3;
  if ($[9] !== state.autoRouted || $[10] !== state.defaultId || $[11] !== state.defaultReasoningLevel || $[12] !== state.items || $[13] !== state.loading || $[14] !== state.selection || $[15] !== store.select || $[16] !== supportsReasoningLevel) {
    t3 = {
      autoRouted: state.autoRouted,
      defaultId: state.defaultId,
      defaultReasoningLevel: state.defaultReasoningLevel,
      items: state.items,
      loading: state.loading,
      onSelect: store.select,
      selectedId: state.selection,
      supportsReasoningLevel
    };
    $[9] = state.autoRouted;
    $[10] = state.defaultId;
    $[11] = state.defaultReasoningLevel;
    $[12] = state.items;
    $[13] = state.loading;
    $[14] = state.selection;
    $[15] = store.select;
    $[16] = supportsReasoningLevel;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const models = t3;
  const t4 = state.defaultReasoningLevel ?? DEFAULT_REASONING;
  const t5 = isAuto ? undefined : state.selection;
  let t6;
  if ($[18] !== models || $[19] !== state.error || $[20] !== supportsReasoningLevel || $[21] !== t4 || $[22] !== t5) {
    t6 = {
      defaultReasoningLevel: t4,
      error: state.error,
      models,
      selectedModelId: t5,
      supportsReasoningLevel
    };
    $[18] = models;
    $[19] = state.error;
    $[20] = supportsReasoningLevel;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Skill / artifact pickers
// ---------------------------------------------------------------------------

/** Prop-ready source for the block's `skills` prop
 *  (`magnus.skills.listPickerItems()`). */
export function useMagnusSkillPicker() {
  const $ = _c(7);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = createListStore(() => client.skills.listPickerItems());
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const store = t0;
  let t1;
  if ($[2] !== store) {
    t1 = store.getState();
    $[2] = store;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    items,
    loading
  } = useStoreState(store, t1);
  let t2;
  if ($[4] !== items || $[5] !== loading) {
    t2 = {
      items,
      loading
    };
    $[4] = items;
    $[5] = loading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** Prop-ready source for the block's `artifacts` prop
 *  (`magnus.artifacts.listPickerItems()`). */
export function useMagnusArtifactPicker() {
  const $ = _c(7);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = createListStore(() => client.artifacts.listPickerItems());
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const store = t0;
  let t1;
  if ($[2] !== store) {
    t1 = store.getState();
    $[2] = store;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    items,
    loading
  } = useStoreState(store, t1);
  let t2;
  if ($[4] !== items || $[5] !== loading) {
    t2 = {
      items,
      loading
    };
    $[4] = items;
    $[5] = loading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

const EMPTY_FEEDBACK_STATE: FeedbackStoreState = {
  loading: false,
  map: {},
  pending: false
};

/** HITL approval resolution over `magnus.toolApprovals.submit`
 *  (`POST /api/sdk/v1/tool-approvals`) — prop-ready for the block's
 *  `approvals` prop. Stable per client. */
export function useMagnusApprovals() {
  const $ = _c(4);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = {
      submit: input => client.toolApprovals.submit(input)
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const approvals = t0;
  let t1;
  if ($[2] !== approvals) {
    t1 = {
      approvals
    };
    $[2] = approvals;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export interface UseMagnusFeedbackResult {
  error?: Error;
  /** Prop-ready source for the block's `feedback` prop; `undefined` until
   *  a chat id exists (no feedback affordances on an uncreated chat). */
  feedback?: MagnusFeedbackSource;
  loading: boolean;
}
export interface UseMagnusFeedbackOptions {
  /** The surface feedback is attributed to. Defaults to the provider's
   *  `surface`; a host that renders a distinct surface under one provider
   *  (e.g. Create inside the standalone web app) passes its own. */
  surface?: AppSurface;
}

/** Thumbs state + submit for one chat over `magnus.feedback.get/submit`,
 *  with a local cache update on submit. */
export function useMagnusFeedback(chatId, options) {
  const $ = _c(12);
  const {
    client,
    surface: providerSurface
  } = useMagnusClient();
  const surface = options?.surface ?? providerSurface;
  let t0;
  if ($[0] !== chatId || $[1] !== client || $[2] !== surface) {
    t0 = chatId ? createFeedbackStore({
      chatId,
      client,
      surface
    }) : null;
    $[0] = chatId;
    $[1] = client;
    $[2] = surface;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const store = t0;
  const state = useStoreState(store, EMPTY_FEEDBACK_STATE);
  let t1;
  bb0: {
    if (!store) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[4] !== state.map || $[5] !== state.pending || $[6] !== store.submit) {
      t2 = {
        map: state.map,
        pending: state.pending,
        submit: store.submit
      };
      $[4] = state.map;
      $[5] = state.pending;
      $[6] = store.submit;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = t2;
  }
  const feedback = t1;
  let t2;
  if ($[8] !== feedback || $[9] !== state.error || $[10] !== state.loading) {
    t2 = {
      error: state.error,
      feedback,
      loading: state.loading
    };
    $[8] = feedback;
    $[9] = state.error;
    $[10] = state.loading;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Chat history
// ---------------------------------------------------------------------------

export interface UseMagnusChatHistoryResult {
  /** The selected chat with hydrated messages; `null` = a fresh chat.
   *  Render the chat component only when this is non-pending (see
   *  `loadingActive`). */
  active: {
    /** In-flight run id on the selected chat — pass to `useMagnus`'s
     *  `activeStreamId` so reopening it resumes the live stream. Null when
     *  no run is active (or the server found it already terminal). */
    activeStreamId?: string | null;
    chatId: string;
    initialMessages: MagnusUIMessage[];
  } | null;
  error?: Error;
  /** Prop-ready source for the block's `history` prop. */
  history: MagnusChatHistorySource;
  /** True while a selected chat's messages are being fetched. */
  loadingActive: boolean;
  /** Pass to `refresh` the list (e.g. after the first turn of a new chat). */
  refresh: () => void;
  /** Remount key for the chat component: changes on selection and on every
   *  "New chat". Use as `key` so the transcript fully resets. */
  sessionKey: string;
  /**
   * Props ready to spread onto `MagnusChat` so a settled turn invalidates
   *  the list: `onFirstStream` puts a brand-new chat into recents, and
   *  `onFinish` re-polls for the server's async auto-title.
   */
  turnInvalidation: {
    onFirstStream: () => void;
    onFinish: () => void;
  };
}

/** Previous-conversations switcher over `magnus.chats.list` + `chats.get`
 *  (selection hydrates messages in the `initialMessages` shape `useMagnus`
 *  accepts). The returned `history` source also carries `onRename` /
 *  `onDelete` (plan 028), so the block's switcher shows its inline
 *  rename/delete row actions out of the box. */
export function useMagnusChatHistory() {
  const $ = _c(26);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = createChatHistoryStore({
      client
    });
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const store = t0;
  let t1;
  let t2;
  if ($[2] !== store) {
    t1 = () => () => store.dispose();
    t2 = [store];
    $[2] = store;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== store) {
    t3 = store.getState();
    $[5] = store;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const state = useStoreState(store, t3);
  const t4 = state.items;
  const t5 = state.loadingList;
  const t6 = store.newChat;
  const t7 = store.select;
  let t8;
  if ($[7] !== store) {
    t8 = bindHistoryActions(store);
    $[7] = store;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== state.items || $[10] !== state.loadingList || $[11] !== store.newChat || $[12] !== store.select || $[13] !== t8) {
    t9 = {
      items: t4,
      loading: t5,
      onNewChat: t6,
      onSelect: t7,
      ...t8
    };
    $[9] = state.items;
    $[10] = state.loadingList;
    $[11] = store.newChat;
    $[12] = store.select;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const history = t9;
  let t10;
  if ($[15] !== store.notifyTurnSettled || $[16] !== store.refresh) {
    t10 = {
      onFirstStream: store.refresh,
      onFinish: store.notifyTurnSettled
    };
    $[15] = store.notifyTurnSettled;
    $[16] = store.refresh;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const turnInvalidation = t10;
  let t11;
  if ($[18] !== history || $[19] !== state.active || $[20] !== state.error || $[21] !== state.loadingActive || $[22] !== state.sessionKey || $[23] !== store.refresh || $[24] !== turnInvalidation) {
    t11 = {
      active: state.active,
      error: state.error,
      history,
      loadingActive: state.loadingActive,
      refresh: store.refresh,
      sessionKey: state.sessionKey,
      turnInvalidation
    };
    $[18] = history;
    $[19] = state.active;
    $[20] = state.error;
    $[21] = state.loadingActive;
    $[22] = state.sessionKey;
    $[23] = store.refresh;
    $[24] = turnInvalidation;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}

// ---------------------------------------------------------------------------
// Uploads
// ---------------------------------------------------------------------------

export interface UseMagnusUploadsResult {
  /** Prop-ready source for the block's `uploads` prop; `undefined` until a
   *  chat id exists (no attach button on an uncreated chat). */
  uploads?: MagnusUploadsSource;
}

/**
 * Composer attachments over `magnus.uploads.*` (plan 023). The source owns
 * the whole direct-upload lifecycle (mint → PUT → finalize, with staged-row
 * cleanup on failure); the block owns the staging state machine. There is no
 * reactive state, so the hook is a memoized factory, not a
 * `useSyncExternalStore` wrapper.
 */
export function useMagnusUploads(chatId) {
  const $ = _c(5);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== chatId || $[1] !== client) {
    t0 = chatId ? createUploadsStore({
      chatId,
      client
    }) : undefined;
    $[0] = chatId;
    $[1] = client;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const uploads = t0;
  let t1;
  if ($[3] !== uploads) {
    t1 = {
      uploads
    };
    $[3] = uploads;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// ---------------------------------------------------------------------------
// Dictation
// ---------------------------------------------------------------------------

export interface UseMagnusDictationResult {
  /** Prop-ready source for the block's `dictation` prop. */
  dictation: MagnusDictationSource;
}

/**
 * Push-to-talk dictation over `magnus.voice.mintToken("stt")` + batched
 * Cartesia STT (plan 027): tap to start recording, tap again to
 * transcribe into the composer. The token mints lazily on the first
 * `start()` (not on mount — a per-mount mint would burn the
 * rate-limited write bucket for users who never dictate) and is cached
 * across turns. Streaming STT over WS is a recorded follow-on.
 */
export function useMagnusDictation() {
  const $ = _c(15);
  const {
    client
  } = useMagnusClient();
  let t0;
  if ($[0] !== client) {
    t0 = createDictationStore({
      client
    });
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const store = t0;
  let t1;
  let t2;
  if ($[2] !== store) {
    t1 = () => () => store.dispose();
    t2 = [store];
    $[2] = store;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== store) {
    t3 = store.getState();
    $[5] = store;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const state = useStoreState(store, t3);
  let t4;
  if ($[7] !== state.error || $[8] !== state.state || $[9] !== state.transcript || $[10] !== store.start || $[11] !== store.stop) {
    t4 = {
      error: state.error,
      start: store.start,
      state: state.state,
      stop: store.stop,
      transcript: state.transcript
    };
    $[7] = state.error;
    $[8] = state.state;
    $[9] = state.transcript;
    $[10] = store.start;
    $[11] = store.stop;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const dictation = t4;
  let t5;
  if ($[13] !== dictation) {
    t5 = {
      dictation
    };
    $[13] = dictation;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}